<template>
  <div style="height: calc(100vh - 8rem)">
    <div class="container-area">
      <div id="chat_container" class="container-contents">
        <v-row no-gutters v-for="(chat, chat_i) in chat_history" :key="chat_i" :justify="chat.type === 'user' ? 'end' : 'start'" style="overflow-x: hidden" :id="`chat_${chat_i}`">
          <div class="container-content" :class="chat.type === 'user' ? 'container-from-right' : 'container-from-left d-flex'">
            <ChatInfo v-if="chat.messages.length > 0" :from="chat.type" :icon="config.logo_url" :name="config.chat_bubble_title" :datetime="chat.datetime" />

            <div v-if="chat.loading" class="chat-contents" style="margin-left: 2rem">
              <LoadingDot />
            </div>

            <div v-else>
              <div
                v-for="(message, message_i) in chat.messages"
                :key="message_i"
                class="chat-contents container-fade"
                :id="`message_${chat_i}_${message_i}`"
                :class="message.type === 'cards' || message.type === 'informative_cards' ? 'card-scroll' : ''"
                :style="message.type === 'cards' || message.type === 'informative_cards' ? '' : 'margin-left: 2rem'"
              >
                <StreamingChatBubbleAi v-if="message.type === 'streaming_text'" :message="message" :is_last_message="chat_i === chat_history.length - 1" />

                <ChatBubbleAi v-else-if="chat.type === 'ai' && message.type === 'text'" :message="message" :is_last_message="chat_i === chat_history.length - 1" />

                <ChatBubbleUser v-else-if="chat.type === 'user' && message.type === 'text'" :text="message.text" />

                <QuickReply v-else-if="message.type === 'quick_replies'" :message="message" :chat_i="chat_i" :message_i="message_i" />

                <Button v-else-if="message.type === 'buttons'" :buttons="message.buttons" />

                <ImageContent v-else-if="message.type === 'image'" :image="message" />

                <Dropdown v-else-if="message.type === 'dropdown'" :buttons="message.buttons" :title="message.title" />

                <Video v-else-if="message.type === 'video'" :video_url="message.video_url" />

                <Card v-else-if="message.type === 'cards'" :message="message" :chat_id="`chat_${chat_i}`" :message_id="`message_${chat_i}_${message_i}`" />

                <InformativeCard v-else-if="message.type === 'informative_cards'" :message="message" :chat_id="`chat_${chat_i}`" :message_id="`message_${chat_i}_${message_i}`" />

                <MultipleSelection v-else-if="message.type === 'multiple_selection'" :multiple_selection="message.multiple_selection" />

                <List v-else-if="message.type === 'list'" :list="message.list" :chat_id="`chat_${chat_i}`" :message_id="`message_${chat_i}_${message_i}`" />

                <Rating v-else-if="message.type === 'rating'" :rating="message.rating" />

                <Audio v-else-if="message.type === 'audio'" :uuid="chat_i" :audio_url="message.audio_url" :type="chat.type" />

                <DateTime v-else-if="message.type === 'date_time'" :datetime="message.date_time" />
              </div>
            </div>
          </div>
        </v-row>
      </div>
    </div>
  </div>
</template>

<script setup>
// global config
import global_config from '@/../config.json'

// Import components
import LoadingDot from './Widgets/LoadingDot'
import ChatInfo from './Widgets/ChatInfo'
import StreamingChatBubbleAi from './Widgets/StreamingChatBubbleAi'
import ChatBubbleAi from './Widgets/ChatBubbleAi'
import ChatBubbleUser from './Widgets/ChatBubbleUser'
import Button from './Widgets/Button'
import ImageContent from './Widgets/ImageContent'
import Video from './Widgets/Video'
import Audio from './Widgets/Audio'
import Rating from './Widgets/Rating'
import DateTime from './Widgets/DateTime'
import Card from './Widgets/Card'
import InformativeCard from './Widgets/InformativeCard'
import MultipleSelection from './Widgets/MultipleSelection'
import List from './Widgets/List'
import Dropdown from './Widgets/Dropdown'
import QuickReply from './Widgets/QuickReply'

// Import modules
import { ref, computed, watch, nextTick } from 'vue'
import { useStore } from 'vuex'

// Init Modules
const store = useStore()

// Data
const config = ref(global_config)
const old_scroll_height = ref(0)
const old_chat_length = ref(0)

// Computed
const chat_history = computed(() => {
  return store.state.chat.chat_history
})

// watch
watch(
  chat_history,
  (newHistory, oldHistory) => {
    var element = document.getElementById('chat_container')
    if (!element) return

    let scrollHeight = element.scrollHeight
    const newLength = newHistory.length
    const oldLength = oldHistory ? oldHistory.length : 0

    nextTick(() => {
      const isUserMessage = newLength === oldLength + 2 && newHistory[newLength - 2]?.type === 'user' && newHistory[newLength - 1]?.loading === true

      const isContentUpdate = newLength === oldLength

      if (isContentUpdate) {
        element.scrollTop = old_scroll_height.value
        old_scroll_height.value = scrollHeight - 30
      } else if (isUserMessage) {
        old_scroll_height.value = scrollHeight - 30
        element.scrollTop = old_scroll_height.value
        old_chat_length.value = newLength
      } else {
        old_scroll_height.value = scrollHeight - 30
        element.scrollTop = old_scroll_height.value
        old_chat_length.value = newLength
      }
    })
  },
  { deep: true }
)
</script>

<style scope>
.container-date {
  padding: 1rem 0;
  height: 2.876rem;
  font-weight: 700;
  font-size: 0.6875rem;
  line-height: 0.875rem;
  color: #828282;
}

.container-area {
  height: 100%;
}

.container-contents {
  max-height: 100%;
  overflow-y: auto;
  scroll-behavior: smooth;
  margin-right: 0.125rem;
}

.container-content {
  margin: 1rem 0;
  flex-direction: column;
  max-width: 100%;
  padding-left: 1rem !important;
  padding-right: 0.875rem;
}

.container-from-left {
  -webkit-animation: slide 1s ease-out forwards;
  animation: slide 1s ease-in-out forwards;
  transform: translateX(-500px);
}

.container-from-right {
  -webkit-animation: slide 0.3s ease-out forwards;
  animation: slide 0.3s ease-in-out forwards;
  transform: translateX(500px);
}

.container-fade {
  -webkit-animation: fade 0.3s ease-out forwards;
  animation: fade 0.3s ease-in-out forwards;
}

.chat-contents {
  width: 100%;
  padding-top: 0.3125rem;
}

.card-scroll {
  width: 24.375rem;
  overflow-x: hidden;
  overflow-y: hidden;
  scroll-behavior: smooth;
  white-space: nowrap;
  margin-left: -1rem;
  padding-left: 3rem;
}

@-webkit-keyframes slide {
  100% {
    transform: translateX(0);
  }
}

@keyframes slide {
  100% {
    transform: translateX(0);
  }
}

@-webkit-keyframes fade {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 100;
  }
}

@keyframes fade {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 100;
  }
}
</style>
